import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronUp } from "lucide-react";
import type { TransitionMeasure, TransitionMeasureMetricType, TransitionMeasureStatus, DocumentMetadata, PolicyTopicData } from "@/contexts/wizard/types";
import type { TransitionActionDef } from "@shared/transitionCatalog";
import { DocumentUploadSection } from "./DocumentUploadSection";

const fieldSelectClassName = "h-10 rounded-2xl border-slate-200 bg-slate-50 px-3 text-sm text-slate-900 shadow-none data-[placeholder]:text-slate-400 focus:ring-0";
const fieldTextareaClassName = "min-h-[96px] rounded-2xl border-0 bg-slate-50 px-3 py-3 text-sm text-slate-900 outline outline-1 outline-offset-[-1px] outline-slate-200 placeholder:text-slate-400 focus-visible:ring-0";

export interface TransitionActionRowProps {
  action: TransitionActionDef;
  measure: TransitionMeasure | undefined;
  metricUnits: string[];
  policyOptions: PolicyTopicData[];
  disabled?: boolean;
  currentTab: "environment" | "social" | "governance";
  fieldIdentifierPrefix: string; // e.g. "transition-carbon-emissions"
  onToggle: (checked: boolean) => void;
  onUpdate: (updates: Partial<TransitionMeasure>) => void;
}

export function TransitionActionRow({
  action, measure, metricUnits, policyOptions, disabled, currentTab, fieldIdentifierPrefix, onToggle, onUpdate,
}: TransitionActionRowProps) {
  const [isOpen, setIsOpen] = useState(false);
  const checked = measure?.checked ?? false;
  const isReadOnlyReuse = !!measure?.reusedFromMeasureId;

  return (
    <Collapsible open={isOpen && checked} onOpenChange={() => checked && setIsOpen((v) => !v)}>
      <div className="overflow-hidden rounded-[22px] border border-slate-200 bg-white shadow-[0px_1px_2px_0px_rgba(0,0,0,0.04)]">
        <div className="flex items-center gap-3 px-4 py-4">
          <Checkbox
            checked={checked}
            onCheckedChange={(value) => { onToggle(!!value); if (value) setIsOpen(true); }}
            disabled={disabled || isReadOnlyReuse}
            className="h-4 w-4 rounded-full border-slate-300 bg-white data-[state=checked]:border-[#00444C] data-[state=checked]:bg-[#00444C] data-[state=checked]:text-white"
          />
          <CollapsibleTrigger asChild>
            <button type="button" disabled={!checked} className="flex flex-1 items-center gap-3 text-left disabled:cursor-default">
              <span className="flex-1 text-[15px] font-semibold leading-6 text-slate-800">{action.name}</span>
              {checked && (isOpen ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />)}
            </button>
          </CollapsibleTrigger>
        </div>
        {action.example && (
          <p className="px-4 pb-2 text-xs text-slate-400">e.g. {action.example}</p>
        )}

        <CollapsibleContent>
          {isReadOnlyReuse ? (
            <div className="border-t border-slate-100 px-4 py-3 text-sm text-slate-500">
              Originally entered under another topic. Edit it there to update this entry.
            </div>
          ) : (
            <div className="space-y-5 border-t border-slate-100 px-4 pb-4 pt-3">
              <div className="grid gap-5 lg:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Related sustainability policy</Label>
                  <Select value={measure?.linkedPolicyId || ""} onValueChange={(value) => onUpdate({ linkedPolicyId: value })} disabled={disabled}>
                    <SelectTrigger className={fieldSelectClassName}><SelectValue placeholder="Select a policy (optional)" /></SelectTrigger>
                    <SelectContent>
                      {policyOptions.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Status</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {([
                      { value: "in-progress", label: "In progress" },
                      { value: "planned", label: "Planned" },
                      { value: "completed", label: "Completed" },
                    ] as { value: TransitionMeasureStatus; label: string }[]).map((opt) => (
                      <Button
                        key={opt.value}
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={disabled}
                        onClick={() => onUpdate({ status: opt.value })}
                        className={`h-9 rounded-md px-3 text-xs font-semibold shadow-none ${
                          measure?.status === opt.value
                            ? "bg-emerald-100 text-emerald-600 outline outline-1 outline-offset-[-1px] outline-current"
                            : "outline outline-1 outline-offset-[-1px] outline-slate-200 text-slate-900 hover:bg-slate-50"
                        }`}
                      >
                        {opt.label}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Do you have a metric to track this action?</Label>
                <Select
                  value={measure?.metricType || ""}
                  onValueChange={(value) => onUpdate({ metricType: value as TransitionMeasureMetricType })}
                  disabled={disabled}
                >
                  <SelectTrigger className={fieldSelectClassName}><SelectValue placeholder="Select an option" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quantitative">Yes, a quantitative metric</SelectItem>
                    <SelectItem value="qualitative">Yes, a qualitative metric</SelectItem>
                    <SelectItem value="both">Yes, both quantitative and qualitative</SelectItem>
                    <SelectItem value="none">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {(measure?.metricType === "qualitative" || measure?.metricType === "both") && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Briefly explain your metric</Label>
                  <Textarea
                    value={measure?.qualitativeNote || ""}
                    onChange={(e) => onUpdate({ qualitativeNote: e.target.value })}
                    className={fieldTextareaClassName}
                    disabled={disabled}
                  />
                </div>
              )}

              {(measure?.metricType === "quantitative" || measure?.metricType === "both") && (
                <div className="grid gap-5 lg:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Metric value</Label>
                    <Input
                      type="number"
                      value={measure?.quantitativeValue ?? ""}
                      onChange={(e) => onUpdate({ quantitativeValue: e.target.value === "" ? undefined : Number(e.target.value) })}
                      disabled={disabled}
                      className="h-10 rounded-2xl border-slate-200 bg-slate-50 px-3 text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Unit</Label>
                    <Select value={measure?.quantitativeUnit || ""} onValueChange={(value) => onUpdate({ quantitativeUnit: value })} disabled={disabled}>
                      <SelectTrigger className={fieldSelectClassName}><SelectValue placeholder="Select unit" /></SelectTrigger>
                      <SelectContent>
                        {[...metricUnits, "other (specify)"].map((unit) => <SelectItem key={unit} value={unit}>{unit}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Description (optional)</Label>
                <Textarea
                  placeholder="Briefly explain what you are doing (e.g., what action is taken, who is responsible, and how often it happens)."
                  value={measure?.description || ""}
                  onChange={(e) => onUpdate({ description: e.target.value })}
                  className={fieldTextareaClassName}
                  disabled={disabled}
                />
              </div>

              <DocumentUploadSection
                fieldIdentifier={`${fieldIdentifierPrefix}-${action.id}`}
                documents={measure?.corroborationDocuments || []}
                onDocumentsChange={(docs: DocumentMetadata[]) => onUpdate({ corroborationDocuments: docs })}
                currentTab={currentTab}
                label="Corroboration document (optional)"
              />
            </div>
          )}
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
